"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { CONTACTS } from "@/lib/contacts";

interface NavItem {
  label: string;
  href?: string;
  children?: NavItem[];
}

const NAV: NavItem[] = [
  {
    label: "Inicio",
    children: [
      { label: "Visión general", href: "/docs" },
      { label: "Inicio rápido", href: "/docs/quickstart" },
      { label: "Licencia y atribución", href: "/docs/license" },
    ],
  },
  {
    label: "API Reference",
    children: [
      { label: "GET /matrix", href: "/docs/api/matrix" },
      { label: "GET /life-path", href: "/docs/api/life-path" },
      { label: "Errores y límites", href: "/docs/api/errors" },
    ],
  },
  {
    label: "Herramientas",
    children: [
      { label: "SDK", href: "/docs/sdk" },
      { label: "OpenAPI / Postman", href: "/docs/openapi" },
      { label: "Ejemplos de código", href: "/docs/examples" },
    ],
  },
  {
    label: "Otros",
    children: [
      { label: "Estado del servicio", href: "/status" },
      { label: "Kit de prensa", href: "/press" },
    ],
  },
];

export function DocsSidebar() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setOpen(!open)}
        className="lg:hidden fixed bottom-4 left-4 z-40 bg-violet-600 text-white p-3 rounded-full shadow-lg hover:bg-violet-700 transition-colors"
        aria-label="Menú de documentación"
      >
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Backdrop on mobile */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/40 z-30"
        />
      )}

      <aside
        className={[
          "fixed lg:sticky top-16 left-0 z-30 w-72 h-[calc(100vh-4rem)] overflow-y-auto bg-white border-r border-stone-200 p-6 transition-transform",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        ].join(" ")}
      >
        <Link
          href="/docs"
          className="block mb-6 text-sm font-semibold text-stone-900 hover:text-violet-700"
          onClick={() => setOpen(false)}
        >
          📚 Documentación de la API
        </Link>
        <nav className="space-y-6">
          {NAV.map((section) => (
            <div key={section.label}>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-400 mb-2.5">
                {section.label}
              </div>
              <ul className="space-y-0.5">
                {section.children?.map((item) => {
                  const active = pathname === item.href;
                  return (
                    <li key={item.href}>
                      <Link
                        href={item.href!}
                        onClick={() => setOpen(false)}
                        className={[
                          "block px-3 py-1.5 text-sm rounded-lg transition-colors",
                          active
                            ? "bg-violet-50 text-violet-700 font-semibold"
                            : "text-stone-600 hover:text-stone-900 hover:bg-stone-50",
                        ].join(" ")}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
        <div className="mt-8 pt-6 border-t border-stone-200">
          <p className="text-xs text-stone-500 leading-relaxed">
            ¿Encontraste un error en la documentación?{" "}
            <a
              href={`mailto:${CONTACTS.general}?subject=Errata%20en%20la%20documentaci%C3%B3n`}
              className="text-violet-700 hover:underline"
            >
              Escríbenos
            </a>
            .
          </p>
        </div>
      </aside>
    </>
  );
}
